import {GridSortModel} from '@mui/x-data-grid';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {t} from 'i18next';
import _ from 'lodash';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {v4} from 'uuid';
import {ToastMessageConfigType} from '../../../../../api/types/ingestionApi.type';
import {
  useExportReviewProgressReviewer,
  useGetLeftPanelData,
  useGetReportState,
  useGetRPRGridData,
  useLeftPanelReport,
  useSaveReviewerProgressReportData,
} from '../../../../../hooks/reporting/useReporting';
import {
  PAGE_SIZE,
  REPORT_PAGE_SIZE_OPTIONS,
  ToastType,
} from '../../../../../shared/constants';
import {ReviewerProgress} from '../../../../../shared/types';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import StandardReports from '../../StandardReports';
import ArrWaitToCompleteModalWrapper from '../../component/waittocomplete-modal-wrapper/ArrWaitToCompleteModalWrapper';
import {
  ColumnConfig,
  Reports,
  RightPanelConfig,
} from '../../config/reportTypes';
import {
  getAppliedFilterValueObject,
  getDefaultSorting,
  getReviewProgressReviewerExportPayload,
  getSaveReviewProgressReportDataPayload,
  getSaveRPRReportDataPayload,
  mergeApiDataIntoConfig,
  updateApplyButtonState,
} from './Helper';
import './ReviewProgressReviewerContainer.scss';
import ReviewProgressSummary from './component/ReviewProgressSummary';

dayjs.extend(utc);

const stringPath = 'reports.reviewProgressReviewer.';
const DEFAULT_PAGE_SIZE = PAGE_SIZE;

interface PageSortConfigType {
  pageSize: number;
  pageNumber: number;
  sort: GridSortModel;
}

const ReviewProgressReviewerContainer: React.FC = () => {
  const location = useLocation();
  const isSavedReportOpen = useRef<boolean>(false);
  const isResetExecuted = useRef<boolean>(false);
  const initialJsonConfig = useRef<any | null>(null);
  const loadedReportIdFromUrl = useRef<number | null>(null);
  const lastProcessedSavedFilterValues = useRef<Record<string, any> | null>(
    null
  );

  const [savedReportData, setSavedReportData] = useState<{
    reportId: number;
    notificationId?: number;
    reportFilePath?: string;
    reportName?: string;
    reportTypeCode?: string;
  }>({reportId: 0});

  const [savedFilterValues, setSavedFilterValues] = useState<
    Record<string, any>
  >({});

  const [reportConfig, setReportConfig] = useState<any | null>(null);

  const [saveReportPayload, setSaveReportPayload] = useState<{
    reportId: number;
    selectedCustomColumns: number[];
    [key: string]: any;
  }>({
    reportId: 0,
    selectedCustomColumns: [],
  });

  const [paginationSortConfig, setPaginationConfig] =
    useState<PageSortConfigType>({
      pageSize: DEFAULT_PAGE_SIZE,
      pageNumber: 1,
      sort: getDefaultSorting(),
    });

  const paginationSortConfigRef =
    useRef<PageSortConfigType>(paginationSortConfig);

  const [isQueryRun, setIsQueryRun] = useState(false);

  const [reportGridData, setReportGridData] = useState<any[]>([]);
  const [rawApiResponse, setRawApiResponse] = useState<any>(null);
  const [summaryData, setSummaryData] = useState<ReviewerProgress | null>(null);

  const [toastMsgConfig, setToastMsgConfig] = useState<
    ToastMessageConfigType & {show: boolean}
  >({
    show: false,
  });

  const {
    data: leftPanelData,
    isLoading: isLoadingLeftPanelData,
    isError: isErrorLeftPanelData,
    error: leftPanelDataError,
    refetch: fetchLeftPanelData,
  } = useGetLeftPanelData(1);

  const {
    mutateAsync: saveReviewerProgressReportDataRequest,
    isPending: isSavingReportData,
  } = useSaveReviewerProgressReportData();

  /** Get Review Report Data Request */
  const {mutateAsync: getReviewReportRequest} = useGetRPRGridData();

  /** Export Review Progress Assignment Report */
  const {
    mutateAsync: exportReviewProgressReviewerRequest,
    isPending: isExportingReport,
  } = useExportReviewProgressReviewer();

  /** Get Left Panel Report Data Request when saved report is opened */
  const reportIdFromUrl = useMemo(() => {
    const key = location.pathname.split('/')[2];
    if (key === 'ReviewerProgressReport' || key === 'progress-report') {
      const params = new URLSearchParams(location.search);
      const id =
        params.get('ReportID') ??
        params.get('reportId') ??
        params.get('ReportId') ??
        params.get('reportID') ??
        '';
      const parsed = Number(id);
      const reportId = Number.isFinite(parsed) ? parsed : 0;
      if (reportId > 0) {
        isSavedReportOpen.current = true;
      }
      return reportId;
    }
    return 0;
  }, [location.pathname, location.search]);

  const hasReportLabel = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('ReportLabel') !== null;
  }, [location.search]);

  const getReportTitle = () => {
    const params = new URLSearchParams(location.search);
    const urlReportName = params.get('ReportLabel');
    let title = reportConfig!.title;
    if (urlReportName) {
      title = urlReportName;
    }
    return title;
  };

  /** Get Report State when coming from notification URL (without ReportLabel) */
  const {
    refetch: fetchReportState,
    isLoading: isReportStateLoading,
    isError: isReportStateError,
    data: savedReportStateData,
    error: reportStateError,
  } = useGetReportState(
    reportIdFromUrl > 0 && !hasReportLabel ? reportIdFromUrl.toString() : '0',
    reportIdFromUrl > 0 && !hasReportLabel
  );

  const {
    data: leftPanelSavedReportData,
    isLoading: isLoadingSavedLeftPanelReport,
    isError: isErrorSavedLeftPanelReport,
    error: leftPanelSavedReportError,
    refetch: _fetchSavedLeftPanelReportData,
  } = useLeftPanelReport(
    reportIdFromUrl,
    reportIdFromUrl > 0 && hasReportLabel
  );

  const reportKey = useMemo(() => {
    const key = location.pathname.split('/')[2];
    if (key === 'ReviewerProgressReport' || key === 'progress-report') {
      return 'ReviewProgressReviewerReport';
    }
    return key;
  }, [location.pathname]);

  // Use progressReportFilteration from state-report when coming from notification (without ReportLabel)
  // Otherwise use leftPanelSavedReportData from leftpanel-report
  const effectiveSavedReportData = useMemo(() => {
    // When coming from notification (without ReportLabel), use progressReportFilteration from state-report
    if (!hasReportLabel && savedReportStateData?.progressReportFilteration) {
      const filtration = savedReportStateData.progressReportFilteration;
      // Transform progressReportFilteration to match LeftPanelReportResponse structure
      return {
        selectedReviewers: filtration.selectedReviewers ?? [],
        selectedAssignments: filtration.selectedAssignments ?? [],
        selectedCustomColumns: filtration.selectedCustomColumns ?? [],
        reportTypeCode: filtration.reportTypeCode,
      } as any;
    }
    // Otherwise use leftPanelSavedReportData from leftpanel-report
    return leftPanelSavedReportData;
  }, [
    hasReportLabel,
    savedReportStateData?.progressReportFilteration,
    leftPanelSavedReportData,
  ]);

  // Transform review report response to grid data format
  const transformReviewReportData = (response: any) => {
    const gridData: any[] = [];
    const gridSummaryData: any[] = [];
    setRawApiResponse(response);
    if (response?.objProgressDTO && Array.isArray(response.objProgressDTO)) {
      response.objProgressDTO.forEach((progress: any) => {
        // Add assignment group items
        if (
          progress.reveiwerReport_ReviewerProgressList &&
          Array.isArray(progress.reveiwerReport_ReviewerProgressList)
        ) {
          progress.reveiwerReport_ReviewerProgressList.forEach(
            (item: any, index: number) => {
              gridData.push({
                id: `${v4()}_${index}`,
                ...item,
              });
            }
          );
        }
        // Add summary data
        if (
          progress.reviewerReport_ReveiwerProgressSummary &&
          Array.isArray(progress.reviewerReport_ReveiwerProgressSummary)
        ) {
          progress.reviewerReport_ReveiwerProgressSummary.forEach(
            (item: any) => {
              gridSummaryData.push({
                id: `${v4()}_${item.assignmentID || item.assignmentsID}`,
                ...item,
              });
            }
          );
        }
      });
    }
    setSummaryData(gridSummaryData.length > 0 && gridSummaryData[0]);
    return gridData;
  };

  useEffect(() => {
    if (loadedReportIdFromUrl.current === null) {
      loadedReportIdFromUrl.current = reportIdFromUrl;
      loadReportConfig().then(() => {
        if (
          (location.state?.fromReportLanding ||
            location.state?.isBackgroundReport ||
            reportIdFromUrl !== 0) &&
          reportIdFromUrl !== 0
        ) {
          // If ReportLabel is not present, call useGetReportState (notification case)
          // Note: leftpanel-report API is automatically called by useLeftPanelReport hook
          // when reportIdFromUrl > 0, so no need to call it manually
          if (!hasReportLabel) {
            fetchReportState();
          }
          // Always fetch base left panel data to populate Assignment Group tree
          // This is needed even when coming from saved report or notification
          if (!leftPanelData && !isLoadingLeftPanelData) {
            fetchLeftPanelData();
          }
        } else {
          fetchLeftPanelData();
        }
      });
    } else if (
      reportIdFromUrl !== 0 &&
      loadedReportIdFromUrl.current !== null
    ) {
      // id updated in url so reset all state and fetch report data again
      isResetExecuted.current = false;
      setIsQueryRun(false);
      resetStateToInitial();
      setSavedFilterValues({});
      setReportConfig(null);
      lastProcessedSavedFilterValues.current = null;
      setSavedReportData({
        reportId: 0,
        notificationId: 0,
      });
      paginationSortConfigRef.current = {
        pageSize: DEFAULT_PAGE_SIZE,
        pageNumber: 1,
        sort: getDefaultSorting(),
      };
      setPaginationConfig(paginationSortConfigRef.current);
      loadedReportIdFromUrl.current = reportIdFromUrl;

      // If ReportLabel is not present, call useGetReportState (notification case)
      // For notification cases, we don't call leftpanel-report API - we use progressReportFilteration from state-report
      // For saved report cases (with ReportLabel), leftpanel-report API is called by useLeftPanelReport hook
      if (!hasReportLabel) {
        fetchReportState();
        // For notification cases, fetch leftPanelData directly after state-report (no need to wait for leftpanel-report)
        if (!leftPanelData && !isLoadingLeftPanelData) {
          fetchLeftPanelData();
        }
      }
      // For saved report cases, leftPanelData will be fetched after leftpanel-report completes
      // See useEffect for leftPanelSavedReportData below
    }
  }, [reportIdFromUrl]);

  useEffect(() => {
    if (isErrorLeftPanelData && leftPanelDataError) {
      setToastMsgConfig({
        show: true,
        type: ToastType.Error,
        message:
          (leftPanelDataError as any)?.response?.data?.title ??
          t(stringPath + 'reportConfigAPIError'),
      });
    }
  }, [isErrorLeftPanelData, leftPanelDataError]);

  useEffect(() => {
    if (
      !isLoadingLeftPanelData &&
      !isErrorLeftPanelData &&
      leftPanelData &&
      initialJsonConfig.current
    ) {
      // Always merge config with leftPanelData to populate Assignment Group tree
      // This ensures the tree is populated even if effectiveSavedReportData is null or still loading
      setReportConfig(
        mergeApiDataIntoConfig(
          initialJsonConfig.current,
          leftPanelData,
          effectiveSavedReportData
        )
      );

      // Only wait for saved report data if we're still loading it AND we have a reportId
      // If it's null after loading completes, proceed anyway (notification link without ReportLabel)
      // For notification case, we don't wait for leftpanel-report since we use state-report data
      if (
        isLoadingSavedLeftPanelReport &&
        effectiveSavedReportData == null &&
        reportIdFromUrl !== 0 &&
        hasReportLabel // Only wait if coming from saved report (with ReportLabel)
      ) {
        // Wait for saved report data to finish loading before setting filter values
        return;
      }

      // When coming from a link, just set the saved report data
      // Don't call assign-group-grid-result API - data will come from review-report API after notification

      if (location.state?.isBackgroundReport || reportIdFromUrl !== 0) {
        setSavedReportData({
          reportId: reportIdFromUrl,
          reportTypeCode:
            effectiveSavedReportData?.reportTypeCode ||
            leftPanelData?.reportTypeCode?.toString(),
        });
        setSavedFilterValues(
          getAppliedFilterValueObject(leftPanelData, effectiveSavedReportData)
        );
        // Initialize saveReportPayload with saved custom columns
        const savedCustomColumns =
          effectiveSavedReportData?.selectedCustomColumns;
        setSaveReportPayload((prev) => ({
          ...prev,
          reportId: reportIdFromUrl,
          selectedCustomColumns:
            savedCustomColumns && savedCustomColumns.length > 0
              ? savedCustomColumns
              : null,
        }));
        // Don't call fetchReportData here - data will be loaded via review-report API
      }
    }
  }, [
    leftPanelData,
    isLoadingLeftPanelData,
    isErrorLeftPanelData,
    reportKey,
    effectiveSavedReportData,
    isLoadingSavedLeftPanelReport,
    reportIdFromUrl,
    location.state,
    hasReportLabel,
  ]);

  useEffect(() => {
    // This useEffect only runs for saved report cases (with ReportLabel)
    // For notification cases (without ReportLabel), we use state-report data directly
    if (!hasReportLabel) {
      return;
    }
    const isNewNavigation = loadedReportIdFromUrl.current !== reportIdFromUrl;

    if (
      !isLoadingSavedLeftPanelReport &&
      leftPanelSavedReportData &&
      !isErrorSavedLeftPanelReport
    ) {
      // Skip if this is stale data from previous navigation
      if (isNewNavigation && loadedReportIdFromUrl.current !== null) {
        return;
      }

      if (leftPanelData && initialJsonConfig.current) {
        setReportConfig(
          mergeApiDataIntoConfig(
            initialJsonConfig.current,
            leftPanelData,
            effectiveSavedReportData
          )
        );
        setSavedFilterValues(
          getAppliedFilterValueObject(leftPanelData, effectiveSavedReportData)
        );
        // Initialize saveReportPayload with saved custom columns
        const savedCustomColumns =
          effectiveSavedReportData?.selectedCustomColumns;
        setSaveReportPayload((prev) => ({
          ...prev,
          reportId: reportIdFromUrl,
          selectedCustomColumns:
            savedCustomColumns && savedCustomColumns.length > 0
              ? savedCustomColumns
              : [],
        }));
      } else {
        fetchLeftPanelData();
      }
    } else if (
      !isLoadingSavedLeftPanelReport &&
      !leftPanelSavedReportData &&
      reportIdFromUrl !== 0
    ) {
      if (isNewNavigation && loadedReportIdFromUrl.current !== null) {
        return;
      }
      // When leftpanel-report API returns null/empty, fetch left panel data
      // This ensures proper sequencing: leftpanel-report first, then left-panel
      if (!leftPanelData && !isLoadingLeftPanelData) {
        fetchLeftPanelData();
      }
    } else if (
      leftPanelSavedReportError &&
      isErrorSavedLeftPanelReport &&
      !isLoadingSavedLeftPanelReport
    ) {
      // Skip if this is stale data from previous navigation
      if (isNewNavigation && loadedReportIdFromUrl.current !== null) {
        return;
      }
      // If API errors, still try to fetch left panel data to proceed
      // But only after leftpanel-report has completed (even with error)
      if (!leftPanelData && !isLoadingLeftPanelData) {
        fetchLeftPanelData();
      }
      setToastMsgConfig({
        show: true,
        type: ToastType.Error,
        message:
          (leftPanelSavedReportError as any)?.response?.data?.title ??
          t(stringPath + 'reportConfigAPIError'),
      });
    }
  }, [
    leftPanelSavedReportData,
    isLoadingSavedLeftPanelReport,
    isErrorSavedLeftPanelReport,
    leftPanelSavedReportError,
    reportIdFromUrl,
    leftPanelData,
  ]);

  // Handle useGetReportState response when coming from notification (without ReportLabel)
  useEffect(() => {
    if (!isReportStateLoading && !isReportStateError && savedReportStateData) {
      // For notification cases (without ReportLabel), we use state-report data
      // No need to call leftpanel-report API - data is already in progressReportFilteration

      // Check if reviewerProgressReportData exists in the response (notification case)
      if (savedReportStateData.reviewerProgressReportData) {
        // Use reviewerProgressReportData directly from savedReportStateData
        const response = {
          objProgressDTO: savedReportStateData.reviewerProgressReportData,
        };
        const transformedData = transformReviewReportData(response);
        setReportGridData(transformedData);
        setIsQueryRun(true);
        // Filter values will be set via getAppliedFilterValueObject when leftPanelData is available
        // This ensures proper transformation of the data
        // effectiveSavedReportData already uses progressReportFilteration from state-report
      } else if (!isReportStateLoading && isReportStateError) {
        setToastMsgConfig({
          show: true,
          type: ToastType.Error,
          message:
            (reportStateError as any)?.response?.data?.title ??
            t(stringPath + 'reportStateAPIError'),
        });
      }
    }
  }, [
    isReportStateError,
    isReportStateLoading,
    savedReportStateData,
    reportIdFromUrl,
    leftPanelData,
    fetchLeftPanelData,
  ]);

  const baseColumns = useMemo<ColumnConfig[]>(() => {
    // Define columns based on the API response structure
    return [
      {
        field: 'reviewer_Name',
        headerName: t(stringPath + 'reviewer_Name'),
        flex: 1,
        minWidth: 160,
        isVisible: true,
        index: 0,
      },
      {
        field: 'assignment_Name',
        headerName: t(stringPath + 'assignment_Name'),
        flex: 1,
        minWidth: 160,
        isVisible: true,
        index: 1,
      },
      {
        field: 'assignment_Path',
        headerName: t(stringPath + 'assignment_Path'),
        flex: 1,
        minWidth: 180,
        isVisible: true,
        index: 2,
      },
      {
        field: 'assignmentLevel',
        headerName: t(stringPath + 'assignmentLevel'),
        flex: 1,
        minWidth: 160,
        isVisible: true,
        index: 3,
      },
      {
        field: 'assignedDocs',
        headerName: t(stringPath + 'assignedDocs'),
        flex: 1,
        minWidth: 170,
        isVisible: true,
        index: 4,
      },
      {
        field: 'completedDocsByMe',
        headerName: t(stringPath + 'completedDocsByMe'),
        flex: 1,
        minWidth: 230,
        isVisible: true,
        index: 5,
      },
      {
        field: 'completedDocs',
        headerName: t(stringPath + 'completedDocs'),
        flex: 1,
        minWidth: 230,
        isVisible: true,
        index: 6,
      },
      {
        field: 'totalCompletedDocs',
        headerName: t(stringPath + 'totalCompletedDocs'),
        flex: 1,
        minWidth: 240,
        isVisible: true,
        index: 7,
      },
      {
        field: 'remainingDocs',
        headerName: t(stringPath + 'remainingDocs'),
        flex: 1,
        minWidth: 170,
        isVisible: true,
        index: 8,
      },
      {
        field: 'inProgressDocs',
        headerName: t(stringPath + 'inProgressDocs'),
        flex: 1,
        minWidth: 170,
        isVisible: true,
        index: 9,
      },
      {
        field: 'percentage_Completed_report',
        headerName: t(stringPath + 'percentage_Completed_report'),
        flex: 1,
        minWidth: 150,
        isVisible: true,
        index: 10,
      },
      {
        field: 'audio_hrs_total',
        headerName: t(stringPath + 'audio_hrs_total'),
        flex: 1,
        minWidth: 220,
        isVisible: true,
        index: 11,
      },
      {
        field: 'totalAudioDocsCompleted',
        headerName: t(stringPath + 'totalAudioDocsCompleted'),
        flex: 1,
        minWidth: 230,
        isVisible: true,
        index: 12,
      },
      {
        field: 'totalAudioDocsDistributed',
        headerName: t(stringPath + 'totalAudioDocsDistributed'),
        flex: 1,
        minWidth: 240,
        isVisible: true,
        index: 13,
      },
      {
        field: 'audio_hrs_completed',
        headerName: t(stringPath + 'audio_hrs_completed'),
        flex: 1,
        minWidth: 230,
        isVisible: true,
        index: 14,
      },
      {
        field: 'totalChatDocs',
        headerName: t(stringPath + 'totalChatDocs'),
        flex: 1,
        minWidth: 170,
        isVisible: true,
        index: 15,
      },
      {
        field: 'chatDocsCompleted',
        headerName: t(stringPath + 'chatDocsCompleted'),
        flex: 1,
        minWidth: 190,
        isVisible: true,
        index: 16,
      },
      {
        field: 'totalChatDuration',
        headerName: t(stringPath + 'totalChatDuration'),
        flex: 1,
        minWidth: 190,
        isVisible: true,
        index: 17,
      },
      {
        field: 'total_Review_Time_Spent_report',
        headerName: t(stringPath + 'total_Review_Time_Spent_report'),
        flex: 1,
        minWidth: 320,
        isVisible: true,
        index: 18,
      },
      {
        field: 'remaining_time_hrs_report',
        headerName: t(stringPath + 'remaining_time_hrs_report'),
        flex: 1,
        minWidth: 300,
        isVisible: true,
        index: 19,
      },
      {
        field: 'tagged_Responsive',
        headerName: t(stringPath + 'tagged_Responsive'),
        flex: 1,
        minWidth: 210,
        isVisible: true,
        index: 20,
      },
      {
        field: 'tagged_Privileged',
        headerName: t(stringPath + 'tagged_Privileged'),
        flex: 1,
        minWidth: 210,
        isVisible: true,
        index: 21,
      },
    ];
  }, [t]);

  const applyColumnVisibility = useCallback(
    (baseCols: ColumnConfig[], hiddenIndexes: number[]) => {
      const hiddenSet = new Set(hiddenIndexes ?? []);
      return (baseCols ?? []).map((col: ColumnConfig) => {
        const idx = col?.index;
        if (typeof idx === 'number' && hiddenSet.has(idx)) {
          return {...col, isVisible: false};
        }
        return col;
      });
    },
    []
  );

  const effectiveSelectedCustomColumns = useMemo(() => {
    const fromSavedData = effectiveSavedReportData?.selectedCustomColumns ?? [];
    // Priority: user selection (even if empty []) > saved data > API sourceColumnList > default []
    // null means no user selection yet, [] means user explicitly selected all columns (no hidden)

    const raw =
      saveReportPayload.selectedCustomColumns !== null
        ? saveReportPayload.selectedCustomColumns // User has made a selection (could be [] for all visible)
        : fromSavedData.length > 0
          ? fromSavedData
          : Array.isArray(rawApiResponse?.sourceColumnList) &&
              rawApiResponse.sourceColumnList.length > 0
            ? rawApiResponse.sourceColumnList
            : [];

    // Normalize: remove duplicates
    const base = Array.isArray(raw) ? raw : [];
    const nums = base
      .map((x: any) => Number(x))
      .filter((n: any) => Number.isFinite(n))
      .map((n: number) => Math.trunc(n));
    return Array.from(new Set(nums));
  }, [
    effectiveSavedReportData?.selectedCustomColumns,
    saveReportPayload.selectedCustomColumns,
  ]);

  const columns = useMemo<ColumnConfig[]>(() => {
    return applyColumnVisibility(baseColumns, effectiveSelectedCustomColumns);
  }, [baseColumns, effectiveSelectedCustomColumns, applyColumnVisibility]);

  const handleColumnToggleAction = useCallback(
    (columnId: string) => {
      setSaveReportPayload((prev) => {
        // RightPanel passes field name, but we use index as unique key
        const columnIdStr = String(columnId).trim();

        // Find the column by field name in baseColumns
        // Use filter to ensure we get exactly one match
        const matchingColumns = baseColumns.filter(
          (col) => String(col.field).trim() === columnIdStr
        );

        // Must have exactly one match - if not, don't update
        if (matchingColumns.length !== 1) {
          return prev;
        }

        const targetCol = matchingColumns[0];

        // Verify the column has a valid index (unique key)
        if (typeof targetCol.index !== 'number') {
          return prev;
        }

        // Use index as the unique identifier for toggling
        const targetIndex = targetCol.index;
        // If null, initialize with DEFAULT_HIDDEN_COLUMNS to start from default state
        const currentHiddenColumns =
          prev.selectedCustomColumns !== null ? prev.selectedCustomColumns : [];

        // Toggle: if index is in hidden list, remove it (show column); otherwise add it (hide column)
        const isCurrentlyHidden = currentHiddenColumns.includes(targetIndex);
        const nextHiddenColumns = isCurrentlyHidden
          ? currentHiddenColumns.filter((idx) => idx !== targetIndex)
          : [...currentHiddenColumns, targetIndex];

        // Ensure uniqueness and sort for consistency
        const uniqueNext = Array.from(new Set(nextHiddenColumns)).sort(
          (a, b) => a - b
        );

        return {
          ...prev,
          selectedCustomColumns: uniqueNext, // Can be [] if all columns are visible
        };
      });
    },
    [baseColumns]
  );

  const handleExportClick = useCallback(async () => {
    if (!reportConfig || !leftPanelData || !isQueryRun) return;
    const exportPayload = getReviewProgressReviewerExportPayload(
      reportConfig,
      savedFilterValues,
      leftPanelData,
      effectiveSelectedCustomColumns,
      savedReportData.reportTypeCode
    );

    try {
      await exportReviewProgressReviewerRequest(exportPayload);
      setToastMsgConfig({
        show: true,
        type: ToastType.Success,
        message: t('reports.common.reportExportSuccess'),
      });
    } catch (error: any) {
      setToastMsgConfig({
        show: true,
        type: ToastType.Error,
        message:
          error?.response?.data?.title ?? t('reports.common.reportExportError'),
      });
    }
  }, [
    reportConfig,
    leftPanelData,
    isQueryRun,
    savedFilterValues,
    paginationSortConfig,
    exportReviewProgressReviewerRequest,
    effectiveSelectedCustomColumns,
    t,
    savedReportData,
  ]);

  const payloadForSaveReport = useMemo(() => {
    if (reportConfig && savedReportData.reportId > 0 && leftPanelData) {
      return getSaveRPRReportDataPayload(
        reportConfig!,
        {
          ...savedFilterValues,
          selectedCustomColumns: effectiveSelectedCustomColumns,
        },
        leftPanelData,
        savedReportData.reportId,
        paginationSortConfig.pageNumber,
        paginationSortConfig.pageSize,
        savedReportData.reportTypeCode
      );
    }
    return null;
  }, [
    reportConfig,
    leftPanelData,
    savedReportData,
    savedFilterValues,
    paginationSortConfig,
    effectiveSelectedCustomColumns,
  ]);

  const rightPanelHeaderConfig = useMemo<RightPanelConfig>(() => {
    return {
      title: t(stringPath + 'title'),
      subTitle: t(stringPath + 'dateAsOf', {
        date: dayjs().utc().format('YYYY/MM/DD HH:mm:ss'),
      }),
      showUploadIcon: true,
      showSaveIcon: true,
      showColumnsIcon: true,
      showActionButton: false,
      onUploadClick: handleExportClick,
    };
  }, [t, payloadForSaveReport, handleExportClick]);

  const totalRecordsCount = useMemo(() => {
    return reportGridData.length;
  }, [reportGridData]);

  const loadReportConfig = useCallback(async () => {
    if (!reportKey) return;
    try {
      const data: Reports = await import(`../../config/${reportKey}.json`);
      initialJsonConfig.current = data;
      return data;
    } catch (error) {
      setToastMsgConfig({
        show: true,
        type: ToastType.Error,
        message: t(stringPath + 'reportConfigAPIError'),
      });
    }
  }, [reportKey]);

  const handleFilterChange = useCallback((values: Record<string, any>) => {
    setReportConfig((prevReportConfig: Reports | null) => {
      if (!prevReportConfig) return prevReportConfig;
      return updateApplyButtonState(prevReportConfig, values);
    });
  }, []);

  useEffect(() => {
    if (
      reportConfig &&
      savedFilterValues &&
      Object.keys(savedFilterValues).length > 0
    ) {
      const savedFilterValuesKey = JSON.stringify(savedFilterValues);
      const isNewSavedFilterValues =
        lastProcessedSavedFilterValues.current === null ||
        JSON.stringify(lastProcessedSavedFilterValues.current) !==
          savedFilterValuesKey;

      if (isNewSavedFilterValues) {
        lastProcessedSavedFilterValues.current = savedFilterValues;
        handleFilterChange(savedFilterValues);
      }
    }
  }, [savedFilterValues, reportConfig, handleFilterChange]);

  const resetStateToInitial = () => {
    setReportGridData([]);
    setSummaryData(null);
  };

  const executeSaveReviewProgressGridData = (values: Record<string, any>) => {
    if (!leftPanelData) return;
    const savePayload = getSaveReviewProgressReportDataPayload(
      reportConfig,
      values,
      leftPanelData,
      paginationSortConfig.pageNumber,
      paginationSortConfig.pageSize
    );

    setIsQueryRun(false);
    resetStateToInitial();
    saveReviewerProgressReportDataRequest(savePayload, {
      onSuccess: (data) => {
        paginationSortConfigRef.current = {
          pageSize: DEFAULT_PAGE_SIZE,
          pageNumber: 1,
          sort: getDefaultSorting(),
        };
        setPaginationConfig(paginationSortConfigRef.current);
        setSavedReportData({
          reportId: data.reportID,
          notificationId: data.notificationID,
          reportFilePath: data.reportFilePath,
          reportName: data.reportName,
          reportTypeCode: data.reportType || leftPanelData.reportTypeCode,
        });
      },
      onError: (error: any) => {
        setToastMsgConfig({
          show: true,
          type: ToastType.Error,
          message:
            error?.response?.data?.title ??
            t(stringPath + 'runReportFailedMsg'),
        });
        leftPanelData;
      },
    });
  };

  const handleActionClick = async (
    actionKey: string,
    values: Record<string, any>
  ) => {
    if (actionKey === 'applyChanges') {
      setSavedFilterValues(values);
      executeSaveReviewProgressGridData(values);
    } else if (actionKey === 'reset') {
      isResetExecuted.current = true;
      if (initialJsonConfig.current && leftPanelData) {
        // Pass undefined to ensure toggle resets to default (true)
        setReportConfig(
          mergeApiDataIntoConfig(
            initialJsonConfig.current,
            leftPanelData,
            undefined
          )
        );
      }
      setIsQueryRun(false);
      resetStateToInitial();
      setSavedFilterValues({});
      setSavedReportData({
        reportId: 0,
        notificationId: 0,
      });
      lastProcessedSavedFilterValues.current = null;
      paginationSortConfigRef.current = {
        pageSize: DEFAULT_PAGE_SIZE,
        pageNumber: 1,
        sort: getDefaultSorting(),
      };
      setPaginationConfig(paginationSortConfigRef.current);
      if (isQueryRun) fetchLeftPanelData();
    }
  };

  // Don't wait for saved report data if it's not loading and we have leftPanelData
  // This handles the case when coming from notification link without ReportLabel
  // Don't show loading if we have reviewerProgressReportData from state (notification case)
  const hasStateData =
    savedReportStateData?.reviewerProgressReportData &&
    Array.isArray(savedReportStateData.reviewerProgressReportData) &&
    savedReportStateData.reviewerProgressReportData.length > 0;

  if (
    _.isNil(reportConfig) ||
    _.isNil(leftPanelData) ||
    isLoadingLeftPanelData ||
    // Only wait for leftpanel-report for saved report cases (with ReportLabel)
    // For notification cases (without ReportLabel), we use state-report data instead
    (isLoadingSavedLeftPanelReport &&
      reportIdFromUrl !== 0 &&
      hasReportLabel &&
      !leftPanelSavedReportData &&
      !leftPanelData) ||
    (isReportStateLoading &&
      reportIdFromUrl !== 0 &&
      !hasReportLabel &&
      !hasStateData &&
      !savedReportStateData)
  )
    return <ArrLoadingSpinner isOpen overlay />;

  return (
    <>
      <StandardReports
        className="rpr-container"
        title={getReportTitle()}
        infoContent={reportConfig?.infoContent}
        infoModalTitle={reportConfig?.infoModalTitle ?? ''}
        showInfoIcon={reportConfig?.showInfoIcon ?? false}
        reportsConfig={reportConfig?.config as any}
        data={reportGridData}
        mapRows={(data) => data}
        loading={false}
        columns={columns}
        sources={[]}
        selectedOption={null}
        onAction={handleActionClick}
        onFilterChange={handleFilterChange}
        rightPanelHeaderConfig={rightPanelHeaderConfig}
        customHeaderContent={
          summaryData ? (
            <ReviewProgressSummary reviewerProgressSummary={summaryData} />
          ) : undefined
        }
        enableSave={isQueryRun}
        isQueryRun={isQueryRun}
        disableSourceSelection={true}
        validateActionFromParent
        saveReportConfig={payloadForSaveReport}
        onColumnToggleAction={handleColumnToggleAction}
        getRowId={(row) => row.id}
        gridConfig={{
          primaryKey: 'id',
          totalRecords: totalRecordsCount,
          className: 'rpr-report-grid',
          enableCheckbox: false,
          disableVirtualization: false,
          pageSizeOptions: REPORT_PAGE_SIZE_OPTIONS,
          itemsPerPage: paginationSortConfigRef.current.pageSize,
          currentPage: paginationSortConfigRef.current.pageNumber,
          sortModel: paginationSortConfigRef.current.sort,
          paginationMode: 'client',
          sortingMode: 'client',
          onSortModelChange: (value) => {
            paginationSortConfigRef.current = {
              ...paginationSortConfigRef.current,
              sort: value,
            };
            setPaginationConfig(paginationSortConfigRef.current);
          },
          setItemsPerPage: (value) => {
            paginationSortConfigRef.current = {
              ...paginationSortConfigRef.current,
              pageSize: value,
              pageNumber: 1,
            };
            setPaginationConfig(paginationSortConfigRef.current);
          },
          onPageChange: (pageNumber, pageSize) => {
            paginationSortConfigRef.current = {
              ...paginationSortConfigRef.current,
              pageNumber,
              pageSize: pageSize ?? paginationSortConfigRef.current.pageSize,
            };
            setPaginationConfig(paginationSortConfigRef.current);
          },
          getRowHeight: () => 'auto',
          entityType: t('common.entries'),
        }}
        observReportConfigUpdate={
          reportIdFromUrl !== 0 && Object.keys(savedFilterValues).length > 0
            ? true
            : false
        }
        selectedFilterValues={savedFilterValues}
      />
      {(isSavingReportData || isExportingReport) && (
        <ArrLoadingSpinner isOpen overlay />
      )}
      {toastMsgConfig.show && (
        <ArrToastNotification
          onClose={() => setToastMsgConfig({show: false})}
          status={[toastMsgConfig.type ?? ToastType.Success]}
          message={toastMsgConfig.message ?? ''}
          title={toastMsgConfig.title}
        />
      )}
      {savedReportData.reportId && savedReportData.notificationId && (
        <ArrWaitToCompleteModalWrapper
          showClose={false}
          reportId={savedReportData.reportId}
          notificationId={savedReportData.notificationId}
          onComplete={async (reportId) => {
            if (
              savedReportData.reportId === reportId &&
              savedReportData.reportFilePath &&
              savedReportData.reportName
            ) {
              try {
                const response = await getReviewReportRequest({
                  reportID: reportId,
                  reportFilePath: savedReportData.reportFilePath,
                  reportName: savedReportData.reportName,
                });
                const transformedData = transformReviewReportData(response);
                setReportGridData(transformedData);
                setIsQueryRun(true);
              } catch (error: any) {
                setToastMsgConfig({
                  show: true,
                  type: ToastType.Error,
                  message:
                    error?.response?.data?.title ??
                    t(stringPath + 'reportAPIError'),
                });
              }
            }
          }}
        />
      )}
    </>
  );
};

export default ReviewProgressReviewerContainer;
